import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useExport } from '../hooks/useExport';
import type { TimePeriod } from '../types';

interface ExportButtonProps {
    period: TimePeriod;
    siteIds: string[];
}

export const ExportButton: React.FC<ExportButtonProps> = ({ period, siteIds }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { isExporting, exportData } = useExport(period, siteIds);

    const handleExport = async (format: 'csv' | 'png') => {
        await exportData(format);
        setIsOpen(false);
    };

    return (
        <div className="relative">
            <motion.button
                onClick={() => setIsOpen(!isOpen)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isExporting}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border)] text-[var(--text-secondary)] text-[12px] hover:border-[var(--border-strong)] hover:text-[var(--text)] transition-all disabled:opacity-50"
            >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <span>Экспорт</span>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-40"
                            onClick={() => setIsOpen(false)}
                        />
                        <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.95 }}
                            className="absolute right-0 top-full mt-1 bg-[var(--bg-elevated)] border border-[var(--border)] rounded-lg shadow-lg p-1 z-50 min-w-[140px]"
                        >
                            <button
                                onClick={() => handleExport('csv')}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] text-[var(--text)] hover:bg-[var(--bg-hover)] transition-colors text-left"
                            >
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                    <polyline points="14 2 14 8 20 8"/>
                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                </svg>
                                Скачать CSV
                            </button>
                            <button
                                onClick={() => handleExport('png')}
                                className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-[12px] text-[var(--text)] hover:bg-[var(--bg-hover)] transition-colors text-left"
                            >
                                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                    <circle cx="8.5" cy="8.5" r="1.5"/>
                                    <polyline points="21 15 16 10 5 21"/>
                                </svg>
                                Скачать PNG
                            </button>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};